import React, { useState, useEffect } from 'react';
import {
  Users,
  FileText,
  TrendingUp,
  DollarSign,
  Phone,
  Mail,
  Calendar,
  BarChart3,
  PieChart,
  Activity
} from 'lucide-react';
import { adminApi, leadsApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const AdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [analyticsResponse, leadsResponse] = await Promise.all([
        adminApi.getAnalytics(),
        leadsApi.getLeads()
      ]);

      setAnalytics(analyticsResponse.data);
      setRecentLeads(leadsResponse.data.slice(0, 5)); // Latest 5 leads
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load dashboard data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-4 border-4 border-gray-600 border-t-silver rounded-full animate-spin"></div>
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const statsCards = [
    {
      title: 'Total Leads',
      value: analytics?.totalLeads || 0,
      icon: Users,
      color: 'from-blue-600 to-blue-400'
    },
    {
      title: 'Scheduled Meetings',
      value: analytics?.totalSchedules || 0,
      icon: Calendar,
      color: 'from-green-600 to-green-400'
    },
    {
      title: 'New Leads',
      value: analytics?.leadsByStatus?.new || 0,
      icon: Mail,
      color: 'from-purple-600 to-purple-400'
    },
    {
      title: 'Qualified Leads',
      value: analytics?.leadsByStatus?.qualified || 0,
      icon: DollarSign,
      color: 'from-orange-600 to-orange-400'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'qualified': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-silver mb-2">Dashboard</h1>
        <p className="text-gray-400">Welcome back! Here's what's happening with your business.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <div key={index} className="glass-morphism rounded-xl p-6 hover:scale-105 transition-transform duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg bg-gradient-to-r ${stat.color}`}>
                  <IconComponent size={24} className="text-white" />
                </div>
              </div>
              <div className="mb-1">
                <h3 className="text-2xl font-bold text-silver">{stat.value}</h3>
              </div>
              <p className="text-gray-400 text-sm">{stat.title}</p>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Lead Status Chart */}
        <div className="glass-morphism rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-silver flex items-center gap-2">
              <PieChart size={20} />
              Lead Status Distribution
            </h2>
          </div>

          <div className="space-y-4">
            {analytics?.leadsByStatus && Object.entries(analytics.leadsByStatus).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${status === 'new' ? 'bg-blue-500' :
                    status === 'contacted' ? 'bg-yellow-500' :
                      status === 'qualified' ? 'bg-green-500' :
                        'bg-purple-500'
                    }`}></div>
                  <span className="text-silver capitalize">{status}</span>
                </div>
                <div className="text-right">
                  <div className="text-silver font-semibold">{count}</div>
                  <div className="text-xs text-gray-400">
                    {analytics.totalLeads > 0 ? Math.round((count / analytics.totalLeads) * 100) : 0}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lead Sources */}
        <div className="glass-morphism rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-silver flex items-center gap-2">
              <BarChart3 size={20} />
              Lead Sources
            </h2>
          </div>

          <div className="space-y-4">
            {analytics?.leadsBySources && Object.entries(analytics.leadsBySources).map(([source, count]) => (
              <div key={source} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${source === 'contactForm' ? 'bg-blue-500/20' :
                    source === 'newsletter' ? 'bg-green-500/20' :
                      'bg-orange-500/20'
                    }`}>
                    {source === 'contactForm' ? <Phone size={16} className="text-blue-400" /> :
                      source === 'newsletter' ? <Mail size={16} className="text-green-400" /> :
                        <FileText size={16} className="text-orange-400" />}
                  </div>
                  <span className="text-silver">
                    {source === 'contactForm' ? 'Contact Form' :
                      source === 'newsletter' ? 'Newsletter' :
                        'Resource Download'}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-silver font-semibold">{count}</div>
                  <div className="text-xs text-gray-400">
                    {analytics.totalLeads > 0 ? Math.round((count / analytics.totalLeads) * 100) : 0}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Leads */}
      <div className="glass-morphism rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-silver flex items-center gap-2">
            <Activity size={20} />
            Recent Leads
          </h2>
          <button className="text-silver hover:text-white text-sm transition-colors duration-300">
            View All →
          </button>
        </div>

        {recentLeads.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 font-medium text-gray-300">Contact</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-300">Company</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-300">Service</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-300">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-300">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentLeads.map((lead, index) => (
                  <tr key={lead.id} className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors duration-300">
                    <td className="py-3 px-4">
                      <div>
                        <div className="text-silver font-medium">{lead.name}</div>
                        <div className="text-gray-400 text-sm">{lead.email}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-300">{lead.company || 'N/A'}</td>
                    <td className="py-3 px-4 text-gray-300 capitalize">{lead.service || 'General'}</td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                        {lead.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-400 text-sm">
                      {new Date(lead.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Users size={48} className="mx-auto text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-silver mb-2">No leads yet</h3>
            <p className="text-gray-400">Leads will appear here when visitors submit the contact form.</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="glass-morphism rounded-xl p-6 text-center">
          <FileText size={32} className="mx-auto text-blue-400 mb-4" />
          <h3 className="text-lg font-semibold text-silver mb-2">Content Management</h3>
          <p className="text-gray-400 text-sm mb-4">Update website content, services, and case studies</p>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-300">
            Manage Content
          </button>
        </div>

        <div className="glass-morphism rounded-xl p-6 text-center">
          <Users size={32} className="mx-auto text-green-400 mb-4" />
          <h3 className="text-lg font-semibold text-silver mb-2">Lead Management</h3>
          <p className="text-gray-400 text-sm mb-4">Review and manage incoming leads and inquiries</p>
          <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-300">
            View Leads
          </button>
        </div>

        <div className="glass-morphism rounded-xl p-6 text-center">
          <BarChart3 size={32} className="mx-auto text-purple-400 mb-4" />
          <h3 className="text-lg font-semibold text-silver mb-2">Analytics</h3>
          <p className="text-gray-400 text-sm mb-4">View detailed reports and performance metrics</p>
          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors duration-300">
            View Reports
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;